/**
 * AppShell — Phase 3c-2 / FU-A / FU-E global persistent top bar.
 *
 * Wraps every authenticated route's `<Outlet />` (board, home, timesheet,
 * project settings, tenant settings). Auth routes (`/login`, `/signup`,
 * `/forgot-password`) skip this and render their `<Outlet />` standalone —
 * the wrapper logic lives in `src/routes/__root.tsx`.
 *
 * Phase 3.6.B layout (left → right):
 *   1. Project switcher (FU-A) — left side; ⌘K from anywhere.
 *   2. Live clock — ticks every second, centered on lg+, hidden on small.
 *   3. Running-timer pill — emerald pill with elapsed + Stop while a
 *      timer is running; nothing while idle.
 *   4. Notifications bell — lifted back from the Sidebar (FU-E pushed it
 *      onto the rail; P3.6.B pushes it back to the top-right where the
 *      tray's down-and-right popper has more room).
 *   5. Profile menu — lifted back from the Sidebar for the same reason.
 *
 * The Sidebar now owns brand + Create-new + the four primary nav items +
 * the tenant Settings gear. The shell owns project context + time +
 * user-context affordances.
 *
 * The global realtime subscription (notifications + timer frames) is
 * mounted by `<Sidebar>`, which is also always-mounted on authed routes
 * and renders the realtime status pill at the bottom of the rail.
 */

import { Link, useLocation, useMatches, useNavigate } from '@tanstack/react-router'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { TimeEntry } from '@/api/types-bridge'
import { GlobalCommandPalette } from '@/components/GlobalCommandPalette'
import { signOutToLogin } from '@/features/auth/sign-out-redirect'
import { useMe } from '@/features/me/use-me'
import { NotificationsBell } from '@/features/notifications/components/NotificationsBell'
import { useProjects } from '@/features/projects/use-projects'
import { StorageOverLimitBanner } from '@/features/tenant-settings/StorageOverLimitBanner'
import { AgentRunsModal } from '@/features/tenant-settings/agents/AgentRunsModal'
import { isAgentUuid } from '@/features/tenant-settings/agents/use-agent-runs'
import { useRunningTimer, useStopTimer } from '@/features/time/use-running-timer'
import { useStorageOverLimitNotifier } from '@/features/uploads/use-storage-over-limit-notifier'

/* -------------------------------------------------------------------------- */
/*                              tiny SVG icons                                */
/* -------------------------------------------------------------------------- */

function StopIcon({ className }: { className?: string }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <rect x="6" y="6" width="12" height="12" rx="1.5" />
    </svg>
  )
}

/* -------------------------------------------------------------------------- */
/*                                  helpers                                   */
/* -------------------------------------------------------------------------- */

/** Pad a non-negative integer to 2 digits. */
function pad2(n: number): string {
  return String(Math.max(0, Math.floor(n))).padStart(2, '0')
}

/** Format a duration in milliseconds as HH:MM:SS. */
function formatElapsed(startedAtIso: string, now: number): string {
  const started = new Date(startedAtIso).getTime()
  if (Number.isNaN(started)) return '00:00:00'
  const totalSeconds = Math.max(0, Math.floor((now - started) / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`
}

/**
 * Format a Date as `Wed, Apr 29 · 14:23:08`. `Intl.DateTimeFormat` keeps
 * us out of date-fns / luxon for one tiny widget.
 */
function formatClock(
  date: Date,
  dateFmt: Intl.DateTimeFormat,
  timeFmt: Intl.DateTimeFormat,
): { date: string; time: string } {
  return { date: dateFmt.format(date), time: timeFmt.format(date) }
}

/** Truncate a card title for the running-timer pill. */
function truncateTitle(title: string, max = 30): string {
  if (title.length <= max) return title
  return `${title.slice(0, max - 1).trimEnd()}…`
}

/* -------------------------------------------------------------------------- */
/*                              live clock widget                             */
/* -------------------------------------------------------------------------- */

function LiveClock() {
  const { i18n } = useTranslation()
  const [now, setNow] = useState(() => new Date())

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }),
    [i18n.language],
  )
  const timeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    [i18n.language],
  )

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const { date, time } = formatClock(now, dateFmt, timeFmt)
  return (
    <div aria-hidden="true" className="hidden items-baseline gap-2 text-sm text-fg-muted lg:flex">
      <span>{date}</span>
      <span className="text-fg-subtle">·</span>
      <span className="font-mono tabular-nums text-fg">{time}</span>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                            running-timer pill                              */
/* -------------------------------------------------------------------------- */

interface RunningTimerPillProps {
  entry: TimeEntry
  onStop: () => void
  isStopping: boolean
}

function RunningTimerPill({ entry, onStop, isStopping }: RunningTimerPillProps) {
  const { t } = useTranslation('common')
  const [tick, setTick] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  // Count from the real click moment, not the rounded `startedAt` — org
  // rounding can push the rounded value into the future, which froze
  // this clock at 00:00:00 for up to a full rounding window.
  const elapsed = formatElapsed(entry.actualStartedAt ?? entry.startedAt, tick)
  const shorthand = entry.shorthand
  const title = entry.deliverableTitle

  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: <output> implies a form-result semantic; this is a live timer pill, role="status" is the canonical pattern.
      role="status"
      aria-live="polite"
      aria-label={t('appShell.timerPill.elapsedAriaLabel', { elapsed })}
      className="animate-[fadeIn_180ms_ease-out] inline-flex h-8 items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 ps-2.5 pe-1 text-xs font-medium text-emerald-800 shadow-sm dark:border-emerald-700/60 dark:bg-emerald-900/40 dark:text-emerald-200"
    >
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      {shorthand && (
        <span className="font-mono uppercase tracking-wide text-emerald-900 dark:text-emerald-100">
          {shorthand}
        </span>
      )}
      {title && (
        <>
          {shorthand && <span className="text-emerald-300 dark:text-emerald-600">·</span>}
          <span className="max-w-[14rem] truncate">{truncateTitle(title)}</span>
        </>
      )}
      {!shorthand && !title && (
        <span className="text-emerald-700 dark:text-emerald-200">
          {t('appShell.timerPill.tracking')}
        </span>
      )}
      <span className="font-mono tabular-nums text-emerald-900 dark:text-emerald-100">
        {elapsed}
      </span>
      <button
        type="button"
        onClick={onStop}
        disabled={isStopping}
        aria-label={t('appShell.timerPill.stopAriaLabel')}
        title={t('appShell.timerPill.stopTitle')}
        className="ms-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full text-emerald-700 transition-colors hover:bg-emerald-100 hover:text-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-60 dark:text-emerald-200 dark:hover:bg-emerald-800/60 dark:hover:text-emerald-100"
      >
        <StopIcon />
      </button>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                              profile menu                                  */
/* -------------------------------------------------------------------------- */

interface ProfileMenuProps {
  initial: string
  name: string | null
  email: string | null
  avatarUrl: string | null
  onSignOut: () => void
}

function ProfileMenu({ initial, name, email, avatarUrl, onSignOut }: ProfileMenuProps) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const handleMouseDown = (event: globalThis.MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open])

  return (
    <div
      ref={containerRef}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault()
          setOpen(false)
        }
      }}
    >
      <button
        type="button"
        aria-label={t('appShell.profileMenu.buttonAriaLabel')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title={name ?? email ?? t('appShell.profileMenu.fallbackTitle')}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-accent-fg transition-shadow hover:shadow focus:outline-none focus:ring-2 focus:ring-accent"
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            aria-hidden="true"
            className="h-8 w-8 rounded-full object-cover ring-1 ring-border"
          />
        ) : (
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-fg">
            {initial}
          </span>
        )}
      </button>
      {open && (
        <div
          role="menu"
          aria-label={t('appShell.profileMenu.menuAriaLabel')}
          className="absolute end-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-lg border border-border bg-bg-elevated shadow-lg"
        >
          {(name || email) && (
            <div className="border-b border-border px-3 py-2.5">
              {name && <p className="truncate text-sm font-medium text-fg">{name}</p>}
              {email && <p className="truncate text-xs text-fg-muted">{email}</p>}
            </div>
          )}
          <Link
            to="/settings"
            search={{ tab: 'my-profile' }}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block w-full px-3 py-2 text-start text-sm text-fg hover:bg-bg-muted focus:bg-bg-muted focus:outline-none"
          >
            {t('appShell.profileMenu.myProfileLabel')}
          </Link>
          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block w-full px-3 py-2 text-start text-sm text-fg hover:bg-bg-muted focus:bg-bg-muted focus:outline-none"
          >
            {t('appShell.profileMenu.settingsLabel')}
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              onSignOut()
            }}
            className="block w-full border-t border-border px-3 py-2 text-start text-sm text-fg hover:bg-bg-muted focus:bg-bg-muted focus:outline-none"
          >
            {t('appShell.profileMenu.signOutLabel')}
          </button>
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                                 AppShell                                   */
/* -------------------------------------------------------------------------- */

interface AppShellProps {
  children: ReactNode
}

export function AppShell({ children }: AppShellProps) {
  const matches = useMatches()
  const navigate = useNavigate()
  const projects = useProjects()
  const me = useMe()
  const runningTimer = useRunningTimer()
  const stopTimer = useStopTimer()
  // Tells whoever just took the workspace over its storage limit, the
  // moment the server says so.
  useStorageOverLimitNotifier()
  // The global realtime channel (notifications + timer frames) is mounted
  // by the Sidebar — it's the same always-mounted layout chrome and the
  // realtime status pill renders there too. Putting both in one place
  // avoids double-subscribing to the same WS endpoint.

  // Find the deepest match with a `projectId` param. Works for both
  // `/board/$projectId` and `/projects/$projectId/settings`.
  const projectIdFromRoute = (() => {
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const m = matches[i]
      if (!m) continue
      const params = m.params as Record<string, string | undefined> | undefined
      if (params?.projectId) return params.projectId
    }
    return null
  })()

  const activeProject = projectIdFromRoute
    ? (projects.data?.find((p) => p.id === projectIdFromRoute) ?? null)
    : null

  const runningEntry = runningTimer.data ?? null

  const handleStop = () => {
    stopTimer.mutate()
  }

  const userName = me.data?.user.name ?? null
  const userEmail = me.data?.user.email ?? null
  const initialSeed = userName || userEmail || '?'
  const initial = initialSeed.trim().charAt(0).toUpperCase() || '?'
  const avatarUrl: string | null = me.data?.user.avatarUrl ?? null

  // The agent-runs dialog is URL-driven so a budget / failed-run
  // notification can open it from any page. Read from the location (not a
  // route's validated search) because most routes' `validateSearch` knows
  // nothing about this param; the router keeps unknown params in the URL.
  const agentRunsId = useLocation({
    select: (l) => {
      const raw = (l.search as Record<string, unknown> | undefined)?.agentRuns
      return typeof raw === 'string' && raw.length > 0 ? raw : null
    },
  })
  const setAgentRunsParam = (agentUserId: string | null) => {
    void navigate({
      to: '.',
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        agentRuns: agentUserId ?? undefined,
      })) as never,
      // A wiki doc keeps its `#thread-…` fragment; closing replaces the
      // entry so Back doesn't reopen the dialog.
      hash: true,
      replace: agentUserId === null,
    })
  }

  // A hand-edited, non-UUID `?agentRuns=` can never open the dialog; drop it
  // (replace, hash kept) the way a valid one is dropped on close.
  const agentRunsInvalid = agentRunsId != null && !isAgentUuid(agentRunsId)
  // biome-ignore lint/correctness/useExhaustiveDependencies: setAgentRunsParam is re-created per render; only the flag matters
  useEffect(() => {
    if (agentRunsInvalid) setAgentRunsParam(null)
  }, [agentRunsInvalid])

  const handleSignOut = () => {
    void signOutToLogin(() => navigate({ to: '/login' }))
  }

  /**
   * Deep-link to a wiki doc with a comment hash (`thread-<id>` or
   * `page-comment-<id>`). The hash is passed THROUGH the router's
   * `navigate()` so it survives the transition + the legacy → pretty-URL
   * redirect atomically. Previously a raw `history.replaceState` fired
   * synchronously right after `navigate()`, which clobbered the in-flight
   * router transition and required a second click to actually navigate.
   * `<DocPage>` reads `window.location.hash` on mount, so a cross-route
   * landing resolves the hash without a manual event; the post-navigate
   * `hashchange` dispatch covers the same-doc repeat-click case where
   * `<DocPage>` is already mounted (TanStack's internal history update
   * doesn't emit a native `hashchange`).
   */
  const openWikiHash = (docId: string, hash: string) => {
    void navigate({ to: '/wiki/d/$docId', params: { docId }, hash }).then(() => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new HashChangeEvent('hashchange'))
      }
    })
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="sticky top-0 z-30 border-b border-border/70 bg-bg-elevated/80 backdrop-blur-md">
        <div className="mx-auto flex h-14 w-full items-center gap-4 px-4 sm:px-6">
          {/*
           * Project switcher — single canonical project navigation
           * primitive for the entire SPA. Replaces BoardHeader's
           * standalone combobox; ⌘K opens it from anywhere.
           */}
          <GlobalCommandPalette projects={projects.data ?? []} activeProject={activeProject} />

          {/* Live clock — centered on large screens via mx-auto on the spacer */}
          <div className="ms-auto flex flex-1 justify-end lg:justify-center">
            <LiveClock />
          </div>

          {/* Right cluster: timer pill, bell, profile. */}
          <div className="flex shrink-0 items-center gap-2 lg:ms-auto">
            {runningEntry && (
              <RunningTimerPill
                entry={runningEntry}
                onStop={handleStop}
                isStopping={stopTimer.isPending}
              />
            )}
            <NotificationsBell
              onOpenCard={(cardId, projectId, commentId, parentCommentId) => {
                // Resolve the board from the notification's own payload so
                // the deep-link works from anywhere (master board, wiki,
                // settings — not just when already on the card's board).
                // BoardPage resolves the uuid via id-match when the
                // shorthand-match misses.
                if (projectId) {
                  navigate({
                    to: '/board/$projectId',
                    params: { projectId },
                    // `commentId` (comment + comment-mention kinds) scrolls
                    // the card modal's thread to + flashes the specific
                    // comment; BoardPage consumes it once then strips it.
                    search: {
                      selectedDeliverable: cardId,
                      commentId: commentId ?? undefined,
                      parentCommentId: parentCommentId ?? undefined,
                    },
                  })
                } else {
                  // Tenant-wide announcement (or a historical row with no
                  // projectId) — open on the master board, which resolves
                  // the card against its cross-project aggregate. The master
                  // route consumes `commentId` exactly like the board does.
                  navigate({
                    to: '/',
                    search: {
                      selectedDeliverable: cardId,
                      commentId: commentId ?? undefined,
                      parentCommentId: parentCommentId ?? undefined,
                    },
                  })
                }
              }}
              onOpenWikiDoc={(docId) => {
                // Phase 4c wiki-mention deep link — route to the doc
                // page. The doc-meta route fetches its own ACL state,
                // so an unauthorized reader hits the standard
                // forbidden surface rather than a stale modal.
                navigate({
                  to: '/wiki/d/$docId',
                  params: { docId },
                })
              }}
              onOpenWikiThread={(docId, threadId) => {
                openWikiHash(docId, `thread-${threadId}`)
              }}
              onOpenWikiPageComment={(docId, threadId) => {
                openWikiHash(docId, `page-comment-${threadId}`)
              }}
              onOpenChatMention={(conversationId, messageId) => {
                // Chat mention deep link — route to the conversation
                // pane with `focusMessage` as a TanStack search param.
                // The pane's child surfaces (MessageList) consume the
                // param to scroll + flash the matching row on mount;
                // the param is cleared after the focus pass fires once
                // so re-renders don't keep re-firing.
                navigate({
                  to: '/messages/$conversationId',
                  params: { conversationId },
                  search: { focusMessage: messageId },
                })
              }}
              onOpenConversation={(conversationId) => {
                // Channel-membership deep-link — route to the conversation
                // pane. No `focusMessage`; there's no specific message.
                navigate({
                  to: '/messages/$conversationId',
                  params: { conversationId },
                })
              }}
              onOpenAgentRuns={setAgentRunsParam}
              onOpenOrgSettings={() => {
                // storage.over_limit deep-link — land on the storage meter.
                navigate({ to: '/settings', search: { tab: 'my-organization' } })
              }}
            />
            <ProfileMenu
              initial={initial}
              name={userName}
              email={userEmail}
              avatarUrl={avatarUrl}
              onSignOut={handleSignOut}
            />
          </div>
        </div>
      </header>

      <StorageOverLimitBanner />
      <AgentRunsModal agentId={agentRunsId} onClose={() => setAgentRunsParam(null)} />

      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </div>
  )
}
